import { create } from "zustand";
import type { ProviderSelection } from "@rivets/core";
import type { PendingAttachment } from "./useAttachments";

/**
 * Open "New chat" tabs, one per worktree.
 *
 * The tab outlives the selection: looking at another chat, file or worktree
 * leaves it in the strip exactly as it was left — text, agent and attachments.
 * Only closing the tab or sending from it removes it — otherwise a half-written
 * first prompt would vanish the moment the user glanced elsewhere.
 */
export type DraftChat = {
  text: string;
  /** Whether the draft was the conversation in front when its worktree was last left. */
  inFront: boolean;
  /** The agent the composer held when it was last unmounted; absent until then. */
  provider?: ProviderSelection;
  /** Attachments parked by an unmounted composer. Their preview URLs stay live until the draft closes. */
  attachments: PendingAttachment[];
  /** Opened by the Review button, so the tab says so. */
  review?: boolean;
  /** Bumped each time the draft is replaced wholesale, so a composer holding an older one remounts
   * and cannot park its stale tray over the replacement. Absent means 0. */
  generation?: number;
};

type DraftChatsState = {
  drafts: Record<string, DraftChat>;
  /** Opens the tab if needed and records whether it is the conversation in front. */
  show: (worktreeId: string, inFront: boolean) => void;
  setText: (worktreeId: string, text: string) => void;
  /**
   * Keeps what an unmounting composer held. Returns false when the draft is no longer open
   * (closed or sent), in which case the caller still owns — and must release — the attachments.
   */
  park: (worktreeId: string, parked: { provider: ProviderSelection; attachments: PendingAttachment[]; generation?: number }) => boolean;
  /** Replaces the worktree's draft with a pre-written one — the Review button's request and files. */
  seed: (worktreeId: string, seeded: { text: string; attachments: PendingAttachment[]; review?: boolean }) => void;
  close: (worktreeId: string) => void;
};

export const useDraftChats = create<DraftChatsState>()((set, get) => ({
  drafts: {},
  show: (worktreeId, inFront) => set((state) => {
    const current = state.drafts[worktreeId];
    if (!current && !inFront) return state;
    if (current?.inFront === inFront) return state;
    return { drafts: { ...state.drafts, [worktreeId]: { text: "", attachments: [], ...current, inFront } } };
  }),
  setText: (worktreeId, text) => set((state) => {
    const current = state.drafts[worktreeId];
    // Typing into a draft is what makes it open, even in the render before the page records it.
    if (current ? current.text === text : text === "") return state;
    return { drafts: { ...state.drafts, [worktreeId]: { inFront: true, attachments: [], ...current, text } } };
  }),
  park: (worktreeId, { provider, attachments, generation }) => {
    const current = get().drafts[worktreeId];
    if (!current) return false;
    // The draft this composer held was replaced while it was mounted; its tray is not the draft's.
    if (generation !== undefined && generation !== (current.generation ?? 0)) return false;
    set((state) => ({ drafts: { ...state.drafts, [worktreeId]: { ...current, provider, attachments } } }));
    return true;
  },
  seed: (worktreeId, { text, attachments, review }) => set((state) => {
    const replaced = state.drafts[worktreeId];
    for (const attachment of replaced?.attachments ?? []) URL.revokeObjectURL(attachment.previewUrl);
    return { drafts: { ...state.drafts, [worktreeId]: {
      text, attachments, inFront: true, review, generation: (replaced?.generation ?? 0) + 1,
    } } };
  }),
  close: (worktreeId) => set((state) => {
    const closed = state.drafts[worktreeId];
    if (!closed) return state;
    for (const attachment of closed.attachments) URL.revokeObjectURL(attachment.previewUrl);
    const { [worktreeId]: _closed, ...rest } = state.drafts;
    return { drafts: rest };
  }),
}));
